import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createUser, clearError } from '../../store/slices/usersSlice';
import { CreateUserRequest } from '../../types/auth';

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserCreateDialog: React.FC<UserCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { isCreating, error } = useAppSelector((state) => state.users);

  const [formData, setFormData] = useState<CreateUserRequest>({
    username: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
      });
      setConfirmPassword('');
      setFormErrors({});
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.username.trim()) {
      errors.username = 'Benutzername ist erforderlich';
    } else if (formData.username.length < 3) {
      errors.username = 'Benutzername muss mindestens 3 Zeichen haben';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
      errors.username = 'Benutzername darf nur Buchstaben und Zahlen enthalten';
    }

    if (!formData.email.trim()) {
      errors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData.password) {
      errors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 8) {
      errors.password = 'Passwort muss mindestens 8 Zeichen haben';
    } else if (!/[A-Z]/.test(formData.password)) {
      errors.password = 'Passwort muss mindestens einen Großbuchstaben enthalten';
    } else if (!/[a-z]/.test(formData.password)) {
      errors.password = 'Passwort muss mindestens einen Kleinbuchstaben enthalten';
    } else if (!/[0-9]/.test(formData.password)) {
      errors.password = 'Passwort muss mindestens eine Zahl enthalten';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Passwort-Bestätigung ist erforderlich';
    } else if (formData.password !== confirmPassword) {
      errors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof CreateUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRoleChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      role: event.target.value as 'admin' | 'user' | 'viewer',
    }));
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value);
    
    // Clear confirm password error when user starts typing
    if (formErrors.confirmPassword) {
      setFormErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(createUser(formData)).unwrap();
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by Redux slice
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Neuen Benutzer erstellen
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          disabled={isCreating}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Benutzername"
            value={formData.username}
            onChange={handleInputChange('username')}
            error={!!formErrors.username}
            helperText={formErrors.username}
            margin="normal"
            required
            disabled={isCreating}
          />

          <TextField
            fullWidth
            label="E-Mail"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!formErrors.email}
            helperText={formErrors.email}
            margin="normal"
            required
            disabled={isCreating}
          />

          <TextField
            fullWidth
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!formErrors.password}
            helperText={formErrors.password || "Mindestens 8 Zeichen, 1 Großbuchstabe, 1 Kleinbuchstabe, 1 Zahl"}
            margin="normal"
            required
            disabled={isCreating}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isCreating}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Passwort bestätigen"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            margin="normal"
            required
            disabled={isCreating}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={isCreating}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl fullWidth margin="normal" required disabled={isCreating}>
            <InputLabel>Rolle</InputLabel>
            <Select
              value={formData.role}
              onChange={handleRoleChange}
              label="Rolle"
            >
              <MenuItem value="user">Benutzer</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="viewer">Betrachter</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isCreating}
            color="secondary"
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating}
            startIcon={isCreating && <CircularProgress size={20} />}
          >
            {isCreating ? 'Erstelle...' : 'Benutzer erstellen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserCreateDialog;